/**
 * How long the skin reveal takes, and in what order (gate V24C).
 *
 * ## Why the timing is a module and not a component
 *
 * Because every claim worth making about the reveal is a claim about time, and
 * a claim about time asserted through a rendered component is asserted through
 * fake timers, a jsdom and a render tree. Here it is arithmetic: the phases, the
 * order, the total, and what `prefers-reduced-motion` does to all three.
 *
 * ## The show is identical for the three skins
 *
 * Silver Hunter, Golden Wing and Cyber Rainbow carry the same weight - exactly
 * one in three each - and nothing in the product assigns them a rarity. So
 * nothing here takes a definition id, and there is no way for one skin to charge
 * longer, glow harder or arrive with more particles than another. A louder
 * reveal is how an interface teaches a hierarchy the game would then have to
 * deny.
 *
 * ## The capacity is revealed, not celebrated
 *
 * The socket phase draws one to six seats, and its length does not depend on how
 * many there are. Six sockets is 9% and one socket is 25%, and that is a
 * difference in frequency, not in power: sockets are empty, jewels do not exist,
 * and a duel normalises combat to constants. A reveal that got brighter with the
 * count would be promising something the product has decided not to promise.
 *
 * ## Nothing here decides anything
 *
 * The copy was drawn, charged, granted and persisted before this file was
 * reached. Skipping moves a phase. Closing unmounts a div.
 */

export type SkinRevealPhase =
  /** Energy gathering. No skin, no name, no capacity on screen. */
  | 'charge'
  /** The moment it opens. One short beat, no white flash. */
  | 'break'
  /** The PNG lands, at its own colours. */
  | 'skin'
  /** The seats appear, one to six. */
  | 'sockets'
  /** Everything is on screen and holding. */
  | 'done';

export interface SkinRevealStep {
  readonly phase: SkinRevealPhase;
  readonly durationMs: number;
}

export interface SkinRevealOptions {
  /** `(prefers-reduced-motion: reduce)`, read once by the component. */
  readonly reducedMotion: boolean;
}

/**
 * The full sequence, in order.
 *
 * Under reduced motion it is ONE step: the skin and its capacity arrive
 * together in a short fade, and the anticipation is dropped entirely. Nothing is
 * lost - the name, the picture and the number of seats are the information, and
 * all three are on screen either way. A reduced-motion path that merely swapped
 * a class while running the same 2.6 seconds of movement would be a preference
 * ignored politely.
 */
export function skinRevealTimeline({
  reducedMotion,
}: SkinRevealOptions): readonly SkinRevealStep[] {
  if (reducedMotion) {
    return Object.freeze([{ phase: 'skin', durationMs: 220 } as const]);
  }
  return Object.freeze([
    { phase: 'charge', durationMs: 900 } as const,
    { phase: 'break', durationMs: 260 } as const,
    { phase: 'skin', durationMs: 700 } as const,
    { phase: 'sockets', durationMs: 520 } as const,
  ]);
}

/** How long the whole thing runs before it settles on `done`. */
export function skinRevealTotalMs(options: SkinRevealOptions): number {
  return skinRevealTimeline(options).reduce((total, step) => total + step.durationMs, 0);
}

/**
 * Whether the skin's own picture may be on screen in a phase.
 *
 * The anticipation shows nothing recognisable. Not because the outcome is a
 * secret worth keeping - it is decided, persisted and one request away - but
 * because a picture that faded in during the charge would make the two halves
 * of the sequence the same half.
 */
export function showsSkin(phase: SkinRevealPhase): boolean {
  return phase === 'skin' || phase === 'sockets' || phase === 'done';
}

/** Whether the seats may be drawn yet. */
export function showsSockets(phase: SkinRevealPhase): boolean {
  return phase === 'sockets' || phase === 'done';
}

/**
 * The delay of one seat, in milliseconds.
 *
 * Staggered so six seats read as six rather than as a block, and capped so the
 * last one still lands inside the socket phase. Deterministic from the index -
 * a re-render must not reshuffle them, and a test that counts them should not
 * have to tolerate noise.
 */
export function socketPipDelayMs(index: number, { reducedMotion }: SkinRevealOptions): number {
  if (reducedMotion) {
    return 0;
  }
  return Math.min(index, 5) * 70;
}

/**
 * How many drifting motes the anticipation uses.
 *
 * A fixed, modest field: this is a pooled set of absolutely positioned spans
 * animated on `transform` and `opacity`, which is what a compositor can run
 * without touching layout. It does not scale with anything, because there is
 * nothing about the outcome it would be honest to scale with.
 */
export function chargeMoteCount({ reducedMotion }: SkinRevealOptions): number {
  return reducedMotion ? 0 : 18;
}
